import React from 'react';
import { 
  Accordion, 
  AccordionDetails, 
  AccordionSummary, 
  Typography, 
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Box,
  styled
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';
import { Pathology } from '../classes/pathology';
import { resultAvatar } from '../utils/avatar';
import ScrollToTop from './ScrollToTop';

interface GroupedPathologiesProps {
  pathologies: Pathology[];
  urlPrefix: string;
  showScrollToTop?: boolean;
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
  '&:before': {
    display: 'none',
  },
  boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  margin: '10px'
}));

const StyledAccordionSummary = styled(AccordionSummary)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  backgroundColor: bgcolor,
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: '64px',
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    margin: theme.spacing(1.5, 0),
  },
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.9,
  },
}));

const DistrictIcon = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    width: '32px',
    height: '32px',
  },
}));

const joinPath = (...path: string[]) => path.join('/').replace(/\/+/g, '/');

function GroupedPathologies({ pathologies, urlPrefix, showScrollToTop }: GroupedPathologiesProps) {
  // Group pathologies by anatomical district
  const groupedPathologies = React.useMemo(() => {
    const groups = new Map<string, Pathology[]>();
    
    pathologies.forEach((pathology) => {
      const district = pathology.distrettoAnatomico;
      if (!groups.has(district)) {
        groups.set(district, []);
      }
      groups.get(district)!.push(pathology);
    });
    
    // Sort each group alphabetically by title
    groups.forEach((pathologyList) => {
      pathologyList.sort((a, b) => a.title.localeCompare(b.title));
    });
    
    return groups;
  }, [pathologies]);

  // Define the order of anatomical districts
  const districtOrder: Record<string, number> = {
    'Infezioni della Cute e dei Tessuti Molli': 1,
    'Infezioni del Cavo Orale': 2,
    'Infezioni Oculari': 3,
    'Otiti': 4,
    'Alte Vie Respiratorie': 5,
    'Basse Vie Respiratorie': 6,
    'Mastiti': 7,
    'Infezioni delle Vie Urinarie': 8,
    'Infezioni Sessualmente Trasmissibili': 9,
    'Diarrea Infettiva Acuta/Gastroenterite': 10,
  };

  // Get sorted districts
  const sortedDistricts = Array.from(groupedPathologies.keys()).sort((a, b) => {
    const orderA = districtOrder[a] || 999;
    const orderB = districtOrder[b] || 999;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.localeCompare(b);
  });

  if (pathologies.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ width: '100%' }}>
        {sortedDistricts.map((district) => {
          const districtPathologies = groupedPathologies.get(district)!;
          const firstPathology = districtPathologies[0];
          
          return (
            <StyledAccordion key={district}>
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                bgcolor={firstPathology.bgColor()}
              >
                <DistrictIcon>
                  {firstPathology.icon()}
                </DistrictIcon>
                <Typography 
                  variant="h6" 
                  fontWeight="medium"
                  sx={{ color: 'inherit' }}
                >
                  {district} ({districtPathologies.length})
                </Typography>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <List sx={{ width: '100%', padding: 0 }} className='background-light'>
                  {districtPathologies.map((pathology, index) => (
                    <React.Fragment key={pathology.code}>
                      {index > 0 && <Divider variant="inset" component="li" />}
                      <Link 
                        to={joinPath(urlPrefix, pathology.url())} 
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <ListItem alignItems="flex-start" className='background-light'>
                          <ListItemAvatar>
                            <Avatar {...resultAvatar(pathology)}>
                              {pathology.icon()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                component="span"
                                variant="body2"
                                color="rgba(0, 0, 0, 0.7)"
                                fontSize={20}
                                fontWeight={600}
                              >
                                {pathology.title}
                              </Typography>
                            }
                            secondary={pathology.render()}
                          />
                        </ListItem>
                      </Link>
                    </React.Fragment>
                  ))}
                </List>
              </AccordionDetails>
            </StyledAccordion>
          );
        })}
      </Box>
      {showScrollToTop && <ScrollToTop />}
    </>
  );
}

export default GroupedPathologies;
